import { difference, isEmpty, uniq } from 'lodash/fp';
import { v4 as uuidv4 } from 'uuid';

import { parseApiError } from '@portal/common/api/request';
import { hasTranslation } from '@portal/common/locale';
import {
    AI_CHAT_MESSAGE_KIND,
    AI_CHAT_MESSAGE_ROLE,
    isAiChatAssistantMessage,
    isAiChatDataCollectionPickerMessage,
    isAiChatErrorMessage,
    isAiChatFiltersMessage,
    isAiChatManualFiltersMessage,
    isAiChatManualMessage,
    isAiChatQueryResultMessage,
    isAiChatTextMessage,
    isAiChatUserMessage,
} from '@portal/common/models/ai-chat-message';
import { DataProjectId } from '@portal/common/models/data-project';
import { DATA_QUERY_TYPE } from '@portal/common/models/data-query';
import { OPERATION_STATUS } from '@portal/common/models/operation-status';
import {
    AiChatDataCollectionOption,
    AiChatHistoryMessage,
    AiChatSummary,
    DataCollectionResource,
    DataFilters,
    DataGranularityKey,
    DataValue,
} from '@portal/common/types';
import {
    parseHistoryAnswer,
    parseManualFiltersContent,
} from '@portal/common/utility/ai-chat-content';

import _ from '../../locale';
import {
    AiChatActivePayload,
    AiChatDataCollectionPickerMessage,
    AiChatDataCollectionPickerPayload,
    AiChatFeedbackableMessage,
    AiChatFiltersMessage,
    AiChatManualFiltersMessage,
    AiChatMessage,
    AiChatQueryResultMessage,
    AiChatUserTextMessage,
} from './types';

// Re-exported from common so the rest of the chat store keeps a single import site.
export {
    parseAssistantContent,
    parseAssistantFilters,
    parseFilterValue,
    parseQueryResultPayload,
    pickNonEmptyParams,
    parseDataTypes,
    resolveAssistantDataTypes,
    resolveAssistantFilters,
    serializeDataTypes,
    toDataCollectionOptions,
} from '@portal/common/utility/ai-chat-content';

export {
    isAiChatDataCollectionPickerMessage as isDataCollectionPickerMessage,
    isAiChatErrorMessage as isErrorMessage,
    isAiChatFiltersMessage as isFiltersMessage,
    isAiChatManualFiltersMessage as isManualFiltersMessage,
    isAiChatQueryResultMessage as isQueryResultMessage,
    isAiChatTextMessage as isTextMessage,
};

export const isUserTextMessage = (message: AiChatMessage): message is AiChatUserTextMessage =>
    isAiChatTextMessage(message) && isAiChatUserMessage(message);

export const isOpenDataCollectionPickerMessage = (
    message: AiChatMessage
): message is AiChatDataCollectionPickerMessage =>
    isAiChatDataCollectionPickerMessage(message) &&
    message.pickedDataCollectionId == null &&
    !message.isDismissed;

export const isFeedbackableMessage = (
    message: AiChatMessage
): message is AiChatFeedbackableMessage =>
    isAiChatFiltersMessage(message) ||
    isAiChatQueryResultMessage(message) ||
    (isAiChatTextMessage(message) && isAiChatAssistantMessage(message));

export const isRankingResultMessage = (
    message: AiChatMessage
): message is AiChatQueryResultMessage =>
    isAiChatQueryResultMessage(message) && message.queryType === DATA_QUERY_TYPE.RANKING;

export const isAggregationResultMessage = (
    message: AiChatMessage
): message is AiChatQueryResultMessage =>
    isAiChatQueryResultMessage(message) && message.queryType === DATA_QUERY_TYPE.AGGREGATION;

type AiChatFilterBearingMessage =
    | AiChatFiltersMessage
    | AiChatQueryResultMessage
    | AiChatManualFiltersMessage;

export const isFilterBearingMessage = (
    message: AiChatMessage
): message is AiChatFilterBearingMessage =>
    isAiChatFiltersMessage(message) ||
    isAiChatManualFiltersMessage(message) ||
    isRankingResultMessage(message) ||
    isAggregationResultMessage(message);

export const getLastFilterBearingMessage = (
    messages: AiChatMessage[]
): AiChatFilterBearingMessage | null => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
        const message = messages[i];
        if (isFilterBearingMessage(message)) {
            return message;
        }
    }
    return null;
};

export const getRankedFilter = (message: AiChatQueryResultMessage): DataFilters | null => {
    const {
        params,
        data,
        dataTypes: [dataType],
    } = message;
    const rankBy = params.rank_by as DataGranularityKey | undefined;

    if (!rankBy || !data) {
        return null;
    }

    const rankByIndex = data.columns.indexOf(rankBy);

    if (rankByIndex < 0) {
        return null;
    }

    const rankedIds = difference(
        uniq(data.records.map((record) => record[rankByIndex])).filter((id) => id != null),
        data.unlicensed_granularity?.[dataType]?.[rankBy] ?? []
    );

    return isEmpty(rankedIds) ? null : { [rankBy]: rankedIds as DataValue };
};

// Without a ranked set, mirroring the un-narrowed proposed filters would drop the user's
// selection for a query nobody asked for.
export const isMirroredRankingResult = (
    message: AiChatMessage
): message is AiChatQueryResultMessage =>
    isRankingResultMessage(message) &&
    message.status === OPERATION_STATUS.SUCCEEDED &&
    getRankedFilter(message) != null;

export const isMirroredAggregationResult = (
    message: AiChatMessage
): message is AiChatQueryResultMessage =>
    isAggregationResultMessage(message) && message.status === OPERATION_STATUS.SUCCEEDED;

export const canMirrorQueryResultToDataExplorer = (
    message: AiChatMessage
): message is AiChatQueryResultMessage =>
    isMirroredRankingResult(message) || isMirroredAggregationResult(message);

export const getLastDataCollectionId = (messages: AiChatHistoryMessage[]): number | null => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
        const dataCollectionId = messages[i].data_collection_id;
        if (dataCollectionId != null) {
            return dataCollectionId;
        }
    }
    return null;
};

export const buildDataCollectionPickerMessage = (
    payload: AiChatDataCollectionPickerPayload
): AiChatDataCollectionPickerMessage => ({
    ...payload,
    id: `${payload.responseId}-data-collections`,
    role: AI_CHAT_MESSAGE_ROLE.ASSISTANT,
    kind: AI_CHAT_MESSAGE_KIND.DATA_COLLECTION_PICKER,
    pickedDataCollectionId: null,
    isDismissed: false,
});

export const mapHistoryMessagesToChatMessages = (
    messages: AiChatHistoryMessage[]
): AiChatMessage[] =>
    messages.flatMap((message, index): AiChatMessage[] => {
        const id = message.id ?? uuidv4();

        if (isAiChatUserMessage(message)) {
            if (!message.content) {
                return [];
            }

            return [
                {
                    id,
                    role: AI_CHAT_MESSAGE_ROLE.USER,
                    kind: AI_CHAT_MESSAGE_KIND.TEXT,
                    content: message.content,
                },
            ];
        }

        if (isAiChatManualMessage(message)) {
            const manual = parseManualFiltersContent(message);

            return manual
                ? [
                      {
                          id,
                          role: AI_CHAT_MESSAGE_ROLE.MANUAL,
                          kind: AI_CHAT_MESSAGE_KIND.MANUAL_FILTERS,
                          ...manual,
                      },
                  ]
                : [];
        }

        const answer = parseHistoryAnswer(messages, index);
        if (!answer) {
            return [];
        }

        const { parsed, dataCollectionOptions, picker } = answer;
        const reaction = message.feedback?.reaction ?? null;
        const dataCollectionId = message.data_collection_id ?? null;

        if (parsed.kind === AI_CHAT_MESSAGE_KIND.TEXT) {
            return [
                {
                    id,
                    role: AI_CHAT_MESSAGE_ROLE.ASSISTANT,
                    kind: AI_CHAT_MESSAGE_KIND.TEXT,
                    content: parsed.content,
                    dataCollectionId,
                    reaction,
                },
            ];
        }

        const isQueryResult = parsed.kind === AI_CHAT_MESSAGE_KIND.QUERY_RESULT;
        const { queryType, params } = isQueryResult ? parsed : { queryType: undefined, params: {} };
        const pickerMessages: AiChatMessage[] = picker
            ? [
                  {
                      ...buildDataCollectionPickerMessage({
                          content: picker.content,
                          responseId: id,
                          options: picker.options,
                          queryType,
                          params,
                          visualization: parsed.visualization,
                      }),
                      ...picker,
                  },
              ]
            : [];

        if (picker && picker.pickedDataCollectionId == null) {
            return pickerMessages;
        }

        if (!isQueryResult) {
            return [
                ...pickerMessages,
                {
                    id,
                    role: AI_CHAT_MESSAGE_ROLE.ASSISTANT,
                    kind: AI_CHAT_MESSAGE_KIND.FILTERS,
                    dataTypes: parsed.dataTypes,
                    filters: parsed.filters,
                    visualization: parsed.visualization,
                    dataCollectionId,
                    reaction,
                },
            ];
        }

        const chosenOption = dataCollectionOptions.find(
            ({ id: optionId }) => optionId === dataCollectionId
        );
        const dataTypes = chosenOption?.dataTypes ?? parsed.dataTypes;

        return [
            ...pickerMessages,
            {
                id,
                role: AI_CHAT_MESSAGE_ROLE.ASSISTANT,
                kind: AI_CHAT_MESSAGE_KIND.QUERY_RESULT,
                queryType: parsed.queryType,
                dataTypes,
                filters: chosenOption?.filters ?? parsed.filters,
                params: parsed.params,
                visualization: parsed.visualization,
                dataCollectionId,
                dataCollectionOptions,
                status: OPERATION_STATUS.PENDING,
                data: null,
                error: null,
                reaction,
            },
        ];
    });

export const buildActiveChatPayload = (chat: AiChatSummary): AiChatActivePayload => ({
    chatId: chat.id,
    messages: mapHistoryMessagesToChatMessages(chat.messages),
    dataCollectionId: getLastDataCollectionId(chat.messages),
});

// Reads a message string from a parsed API error (bare string or `{ message }`).
const extractApiErrorMessage = (apiError: unknown): string | undefined => {
    if (typeof apiError === 'string') {
        return apiError;
    }

    if (apiError && typeof apiError === 'object' && 'message' in apiError) {
        const message = (apiError as { message?: unknown }).message;
        if (typeof message === 'string') {
            return message;
        }
    }

    return undefined;
};

export const getChatErrorMessage = (error: unknown): string | undefined => {
    const message = extractApiErrorMessage(parseApiError(error));
    if (message !== undefined) {
        return message;
    }

    if (error && typeof error === 'object' && 'message' in error) {
        const originalMessage = (error as { message?: unknown }).message;
        if (typeof originalMessage === 'string') {
            return originalMessage;
        }
    }

    return undefined;
};

// Localizes field references to filter labels. Only actual field keys or
// snake_case tokens qualify, so prose words that collide with a `filter_label_*`
// key (e.g. "dimension", "day") aren't rewritten mid-sentence.
const localizeFilterKeys = (text: string, fieldKeys: ReadonlySet<string>): string =>
    text.replace(/[a-zA-Z][a-zA-Z0-9_]*/g, (token) =>
        (token.includes('_') || fieldKeys.has(token)) && hasTranslation(`filter_label_${token}`)
            ? _.withPrefix('filter_label_', token)
            : token
    );

const escapeMarkdown = (text: string): string => text.replace(/[\\`*_{}[\]()<>#+.!|~-]/g, '\\$&');

const formatErrorPiece = (text: string, fieldKeys: ReadonlySet<string>): string =>
    escapeMarkdown(localizeFilterKeys(text, fieldKeys));

// Builds the markdown shown when a data request fails: the backend's message as a
// heading and any per-field messages as a localized bullet list, falling back to the
// generic table error. Example:
//
//   Invalid ranking:
//
//   * "value" is not a rankable dimension of the queried data type
export const getQueryResultErrorMessage = (error: unknown): string => {
    const apiError = parseApiError(error);
    const message = extractApiErrorMessage(apiError);
    const heading = message && message.trim() ? message.trim() : undefined;
    const details: string[] = [];
    const fieldKeys = new Set<string>();

    if (apiError && typeof apiError === 'object') {
        const { fields } = apiError as { fields?: unknown };

        if (fields && typeof fields === 'object') {
            for (const [key, value] of Object.entries(fields as Record<string, unknown>)) {
                if (typeof value === 'string' && value.trim()) {
                    details.push(value.trim());
                    fieldKeys.add(key);
                }
            }
        }
    }

    if (!heading && isEmpty(details)) {
        return _('dex_chat_table_error');
    }

    // With no per-field details there is nothing to bullet — keep it a single line.
    if (isEmpty(details)) {
        return formatErrorPiece(heading!, fieldKeys);
    }

    // Fall back to the generic heading when only field details came back.
    const title = (heading ?? _('dex_chat_table_error')).replace(/[.:\s]+$/, '');
    const bullets = details.map((detail) => `* ${formatErrorPiece(detail, fieldKeys)}`).join('\n');

    return `${formatErrorPiece(title, fieldKeys)}:\n\n${bullets}`;
};

const SUPPORTED_CHAT_DATA_PROJECT_ID_SET = new Set<number>([
    DataProjectId.GBD,
    DataProjectId.GBD2020,
    DataProjectId.GBD2020_CVD,
    DataProjectId.EXPENDITURE,
    DataProjectId.LUNG_CANCER,
    DataProjectId.POPULATION_FORECAST,
    DataProjectId.RACE_ETHNICITY,
]);

export const isChatSupportedDataCollection = (dataCollection: DataCollectionResource): boolean =>
    SUPPORTED_CHAT_DATA_PROJECT_ID_SET.has(dataCollection.data_project_id);

export const isEmptyQueryResult = ({ status, data }: AiChatQueryResultMessage): boolean =>
    status === OPERATION_STATUS.SUCCEEDED && isEmpty(data?.records);

// The other data collections the query matched, offered when the answer failed or found no data.
export const getAlternativeDataCollectionOptions = (
    message: AiChatQueryResultMessage
): AiChatDataCollectionOption[] =>
    message.status === OPERATION_STATUS.FAILED || isEmptyQueryResult(message)
        ? message.dataCollectionOptions.filter(({ id }) => id !== message.dataCollectionId)
        : [];
